// app/features/ai-workflows/components/PersonalizedUseCaseGeneratingCardComponent.tsx
"use client";

import {
  Brain,
  Calendar,
  Clock,
  FileText,
  Loader2,
  MapPin,
  Sparkles,
  Zap,
} from "lucide-react";
import React, { useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { type Database } from "@/types/supabase";

import { usePersonalizedUseCasesHook } from "../services_and_hooks/usePersonalizedUseCasesHook";
import { UseCaseActionsDropdownComponent } from "./UseCaseActionsDropdownComponent";

type PersonalizedUseCase =
  Database["public"]["Tables"]["personalized_use_cases"]["Row"];

interface PersonalizedUseCaseGeneratingCardProps {
  useCase: PersonalizedUseCase;
}

// Enhanced status configuration for generation steps
const getGenerationStepConfig = (status: string | null) => {
  const configs = {
    // ✅ STEP 1: "generating_problem" → "Enhancing problem"
    generating_problem: {
      label: "Enhancing problem",
      description: "Understanding and refining your specific challenge...",
      progress: 25,
      step: "1/4",
      estimatedMinutes: 5,
      icon: Brain,
      bgColor: "bg-indigo-500/10",
      textColor: "text-indigo-600",
      borderColor: "border-indigo-500/20",
    },

    // ✅ STEP 2: "generating_full_workflows" → "Conceptual workflow" (FIXED: backend sends this second)
    generating_full_workflows: {
      label: "Conceptual workflow",
      description: "Designing your custom AI workflow structure...",
      progress: 50,
      step: "2/4",
      estimatedMinutes: 4,
      icon: Sparkles,
      bgColor: "bg-indigo-500/10",
      textColor: "text-indigo-600",
      borderColor: "border-indigo-500/20",
    },

    // ✅ STEP 3: "conceptual_workflows_generated" → "Generating full workflow" (FIXED: backend sends this third)
    conceptual_workflows_generated: {
      label: "Generating full workflow",
      description: "Building comprehensive workflow solutions...",
      progress: 75,
      step: "3/4",
      estimatedMinutes: 3,
      icon: Zap,
      bgColor: "bg-indigo-500/10",
      textColor: "text-indigo-600",
      borderColor: "border-indigo-500/20",
    },

    // ✅ STEP 4: "generating_content" → "Generating content"
    generating_content: {
      label: "Generating content",
      description: "Creating detailed implementation steps and content...",
      progress: 90,
      step: "4/4",
      estimatedMinutes: 2,
      icon: FileText,
      bgColor: "bg-indigo-500/10",
      textColor: "text-indigo-600",
      borderColor: "border-indigo-500/20",
    },

    // Fallback states
    pending: {
      label: "Initializing",
      description: "Setting up your AI workflow generation...",
      progress: 5,
      step: "1/4",
      estimatedMinutes: 6,
      icon: Clock,
      bgColor: "bg-indigo-500/10",
      textColor: "text-indigo-600",
      borderColor: "border-indigo-500/20",
    },
    regenerating: {
      label: "Regenerating",
      description: "Improving your workflow based on new insights...",
      progress: 50,
      step: "Updating",
      estimatedMinutes: 4,
      icon: Loader2,
      bgColor: "bg-indigo-500/10",
      textColor: "text-indigo-600",
      borderColor: "border-indigo-500/20",
    },
  };

  const config = configs[status as keyof typeof configs];
  if (!config) {
    console.warn(
      `[GeneratingCard] Unknown status: "${status}", falling back to pending`,
    );
    return configs.pending;
  }
  return config;
};

export function PersonalizedUseCaseGeneratingCardComponent({
  useCase,
}: PersonalizedUseCaseGeneratingCardProps) {
  const { deleteUseCase, isDeletingUseCase } = usePersonalizedUseCasesHook();
  const [elapsedTime, setElapsedTime] = useState(0);

  // Debug log to see what status we're getting
  console.log(
    `[GeneratingCard] Current status: "${useCase.generation_status}"`,
  );

  const stepConfig = getGenerationStepConfig(useCase.generation_status);
  const StatusIcon = stepConfig.icon;

  // Calculate elapsed time
  useEffect(() => {
    const startTime = new Date(useCase.created_at).getTime();

    const updateElapsedTime = () => {
      const now = new Date().getTime();
      const elapsed = Math.floor((now - startTime) / 1000);
      setElapsedTime(elapsed);
    };

    updateElapsedTime();
    const interval = setInterval(updateElapsedTime, 1000);

    return () => clearInterval(interval);
  }, [useCase.created_at]);

  const formatElapsedTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const handleDelete = async (useCaseId: string) => {
    console.log(`[GeneratingCard] Deleting use case ${useCaseId}`);
    await deleteUseCase(useCaseId);
  };

  return (
    <div
      className={cn(
        "bg-beige-light rounded-xl p-6 transition-all duration-300 ease-in-out shadow-sm h-full flex flex-col",
      )}
    >
      {/* Header with status and actions */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              "flex items-center justify-center w-10 h-10 rounded-lg",
              stepConfig.bgColor,
            )}
          >
            <StatusIcon
              className={cn("w-5 h-5 animate-pulse", stepConfig.textColor)}
            />
          </div>

          <div>
            <Badge
              variant="secondary"
              className={cn(
                "text-xs font-medium px-2 py-1",
                stepConfig.bgColor,
                stepConfig.textColor,
              )}
            >
              Step {stepConfig.step}
            </Badge>
            <p className="text-sm text-gray-500 mt-1">Just now</p>
          </div>
        </div>

        <UseCaseActionsDropdownComponent
          useCase={useCase}
          onDelete={handleDelete}
          isDeleting={isDeletingUseCase}
        />
      </div>

      {/* Main content */}
      <div className="mb-4 flex-grow">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Generating Your AI Workflow
        </h3>

        {/* Progress section */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className={cn("text-sm font-medium", stepConfig.textColor)}>
              {stepConfig.label}
            </span>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Calendar className="w-3 h-3" />
              <span>~{stepConfig.estimatedMinutes}m</span>
              <span className="text-gray-400">•</span>
              <span>{formatElapsedTime(elapsedTime)}</span>
            </div>
          </div>

          {/* Simple progress bar without percentages */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300 ease-in-out"
              style={{ width: `${stepConfig.progress}%` }}
            />
          </div>

          <p className="text-sm text-gray-600 mb-4">{stepConfig.description}</p>
        </div>

        {/* Context section */}
        <div className="space-y-3">
          {useCase.user_input_problem_statement && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">
                  PROBLEM
                </span>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                {useCase.user_input_problem_statement}
              </p>
            </div>
          )}

          {useCase.user_input_desired_outcome && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Zap className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">
                  DESIRED OUTCOME
                </span>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                {useCase.user_input_desired_outcome}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
